import type { FeatureCollection } from "geojson";
import { Geo, type ValidationFinding } from "@rogoag/geo";
import { polygonPartLabel } from "@domain/Geo/geometry";
import type { Deal } from "@domain/CRM/deal";

/** The source pre-sampling-processor files these problems under in a job's Software Errors. */
export const GEO_VALIDATOR_SOURCE = "Geo-Spatial Validator";

/** One of a job's three geometry layers. */
type JobGeometryLayer = "boundary" | "points" | "zones";

/** One place a geometry problem is about, for pointing it out on the map. */
export interface JobGeometryMark {
	layer: JobGeometryLayer;
	/** 0-based index of the feature at fault in that layer's collection. */
	feature: number;
	/** 0-based index of the polygon at fault within the feature; absent when the whole feature is. */
	part?: number;
	/** `[lon, lat]` of the problem itself, or of the center of the polygon at fault. */
	point: [number, number];
}

/** One problem with a job's geometry. `marks` is empty when the problem is about a layer as a whole — a missing
 *  file, a count, an acreage — rather than anything in particular on the map. */
export interface JobGeometryProblem {
	message: string;
	/** The validator's code for the rule; absent for a problem raised here (a missing file or deal). */
	code?: string;
	marks: JobGeometryMark[];
}

/**
 * A boundary finding's wording, with the polygons it is about named the way the map names them.
 *
 * "Boundary contains a duplicate polygon" says nothing about which two of forty polygons are the
 * pair. The validator says where they are, so name them: that turns the message into something a
 * person can act on.
 */
function describeBoundaryFinding(finding: ValidationFinding, boundary: FeatureCollection): string {
	if (finding.code !== "duplicate_boundary_polygon" || finding.locations.length !== 2) return finding.message;
	const labels = finding.locations.map((location) =>
		// A location with no part names its whole feature, not one polygon of it: there is nothing to label, and
		// guessing the first would point at a polygon that may not be the duplicate.
		location.part === undefined
			? null
			: polygonPartLabel(
					location.feature,
					location.part,
					boundary.features[location.feature]?.geometry.type === "MultiPolygon",
				),
	);
	const [first, second] = labels;
	return first && second ? `${finding.message} (${first} and ${second})` : finding.message;
}

/** A validator finding as a problem, its locations tied to the layer they index into. */
function problemFrom(
	finding: ValidationFinding,
	layer: JobGeometryLayer,
	message = finding.message,
): JobGeometryProblem {
	return { message, code: finding.code, marks: finding.locations.map((location) => ({ layer, ...location })) };
}

/** A problem raised here rather than by the validator, with nothing on the map to point at. */
const unlocated = (message: string): JobGeometryProblem => ({ message, marks: [] });

/** A job's loaded geometry, one FeatureCollection per layer (absent when the job has no such file). */
export interface JobGeometry {
	boundary?: FeatureCollection | null;
	points?: FeatureCollection | null;
	zones?: FeatureCollection | null;
}

type DealRules = Pick<Deal, "sitesType" | "density" | "isPtsRequired" | "syncMethod">;

/** Whether a job must carry sample points: pre-sampling-processor's `JobExtended.is_pts_required`. Agrian may not
 *  give us points; otherwise the deal requires them, or it is an immutable-sync Grid deal, or a ModGrid deal.
 *
 *  NOTE this is only half of the gate the geo validator applies. Its `validate_points` asks
 *  `deal.sites_type == "Grid" and job.is_pts_required()`, so a ModGrid deal answers true here and
 *  is still not point-validated. Callers mirroring the lambda must apply the Grid test too — see
 *  `validateJobGeometry`. The two halves are kept apart because this predicate is the lambda's,
 *  and changing it to fold the Grid test in would make it disagree with its counterpart. */
export function arePointsRequired(deal: DealRules, jobFlags: readonly string[]): boolean {
	if (jobFlags.includes("Agrian")) return false;
	return (
		deal.isPtsRequired ||
		(deal.sitesType === "Grid" && (deal.syncMethod ?? "").toLowerCase().includes("immut")) ||
		deal.sitesType === "ModGrid"
	);
}

/**
 * The problems pre-sampling-processor's `validate_geo_data` would file for this geometry, in its order and wording:
 * the boundary, then the points (Grid jobs that require them), then the zones (Zone jobs). The geometry rules
 * themselves are `@rogoag/geo`'s, shared with that lambda. Empty when the geometry is valid.
 */
export function findJobGeometryProblems(
	geometry: JobGeometry,
	deal: DealRules | null,
	jobFlags: readonly string[],
): JobGeometryProblem[] {
	const { boundary, points, zones } = geometry;
	if (!boundary) return [unlocated("No boundary shapefile attached")];
	const problems = Geo.validateBoundary(boundary).map((f) =>
		problemFrom(f, "boundary", describeBoundaryFinding(f, boundary)),
	);

	// Points: the lambda asks for the deal before anything else, so a job without one says so here.
	if (!deal) return [...problems, unlocated("No deal associated with job")];
	// Both halves, deliberately: validate_points returns early unless
	// `deal.sites_type == "Grid" and job.is_pts_required()`. Dropping the Grid test would make this
	// report problems on ModGrid jobs that the lambda never files — a dialog blocking a save over an
	// error the Geo tab could not then clear.
	if (deal.sitesType === "Grid" && arePointsRequired(deal, jobFlags)) {
		if (!deal.density) problems.push(unlocated("No density specified for deal"));
		else if (!points) problems.push(unlocated("No points shapefile attached"));
		else problems.push(...Geo.validatePoints(boundary, points, deal.density).map((f) => problemFrom(f, "points")));
	}

	if (deal.sitesType === "Zone") {
		if (!deal.density) problems.push(unlocated("No density specified for deal"));
		else if (!zones) problems.push(unlocated("No zones shapefile attached"));
		else problems.push(...Geo.validateZones(boundary, zones, deal.density).map((f) => problemFrom(f, "zones")));
	}
	return problems;
}

/** The wording alone of {@link findJobGeometryProblems}, for a caller that only lists the problems. */
export function validateJobGeometry(
	geometry: JobGeometry,
	deal: DealRules | null,
	jobFlags: readonly string[],
): string[] {
	return findJobGeometryProblems(geometry, deal, jobFlags).map((problem) => problem.message);
}
